import { DateTime } from "luxon";
import { getNextSwiftProcessingDateInUTC } from "../../../../helpers/get-next-swift-processing-date-in-utc";
import { etCalendarDateAtLocalMidnight } from "../../../../helpers/get-today-in-et";
import { isUSFederalHoliday } from "../../../../helpers/is-us-federal-holiday";

export const getNextSwiftProcessingDateInLocal = (): DateTime => {
  // The next processing date is an ET calendar date. Re-express that calendar date at
  // local midnight rather than converting the instant, which would shift the date
  // forward for users east of ET and grey out a still-valid ET processing day.
  return etCalendarDateAtLocalMidnight(getNextSwiftProcessingDateInUTC());
};

const sameDay = (a: DateTime, b: DateTime): boolean => {
  return a.hasSame(b, "day") && a.hasSame(b, "month") && a.hasSame(b, "year");
};

/**
 * This method is passed to our ReactDate picker to ensure our React DatePicker only shows valid funding dates for US ET.
 *
 * IMPORTANT: The dates passed into this method are in the LOCAL timezone.
 *            However, the date the user selects will be in ET timezone.
 *            Selecting July 5th in DatePicker should mean July 5th ET.
 *
 * So, when we check the local date, we use keepLocalTime to retrieve the actual ET date. Example:
 * July 4th on date picker should be unselectable regardless of timezone as it is a US Federal Holiday.
 * However, because July 4th comes in as Japan time, we must use the keepLocalTime option to convert it to ET.
 *
 * Note: We can automatically disable weekends without needing to convert TZ.
 *
 * Note: We pass in the nextSwiftProcessingDateInLocal as an optimization as this method is called for every date in the date picker.
 * @param dateToCheck - The date to check if it is a valid funding date
 * @param nextSwiftProcessingDateInLocal - The next SWIFT processing date in the local timezone
 * @returns
 */
export const filterAvailableDate = (
  dateToCheck: Date,
  nextSwiftProcessingDateInLocal: DateTime,
): boolean => {
  // Note: We must set the time to 0 to ensure isUSFederalHoliday works correctly, otherwise we will be off by 1 day.
  const dateToCheckLocal = DateTime.fromJSDate(dateToCheck)
    .setZone(DateTime.local().zoneName)
    .startOf("day");

  // keepLocalTime will change just the timezone to ET without changing the actual time. (e.g. 9PM PT -> 9PM ET)
  const dateToCheckET = dateToCheckLocal.setZone("America/New_York", { keepLocalTime: true });
  const isDateToCheckUSFederalHoliday = isUSFederalHoliday(dateToCheckET);

  const oneMonthLimit = nextSwiftProcessingDateInLocal.plus({ months: 1 });

  // Special case for when date to check matches the next SWIFT processing date.
  // In this case, if they are the same day, we should allow the user to select it.
  const isSwiftProcessingDateSelectable = sameDay(
    dateToCheckLocal,
    nextSwiftProcessingDateInLocal,
  );

  return (
    !isDateToCheckUSFederalHoliday &&
    !dateToCheckLocal.isWeekend &&
    (dateToCheckLocal > nextSwiftProcessingDateInLocal || isSwiftProcessingDateSelectable) &&
    // Inclusive upper bound: the day exactly one month out was selectable (in the common
    // case) before the anchor became midnight-aligned, and must stay selectable.
    (dateToCheckLocal < oneMonthLimit || sameDay(dateToCheckLocal, oneMonthLimit))
  );
};
